import cn from "@/v2-ui/utils/utils.cn"
import Badge from "@/v2-backoffice-client/components/badge"
import Button, { TooltipButton } from "@/v2-backoffice-client/components/button"
import LicenseFeatureGroupCheckedByMark from "@/v2-backoffice-client/license/featureGroup/compose/LicenseFeatureGroupCheckedByMark"
import LicenseFeatureGroupDetailsHoverCard from "@/v2-backoffice-client/license/featureGroup/compose/LicenseFeatureGroupDetailsHoverCard"
import LicenseFeatureGroupReachIndicator from "@/v2-backoffice-client/license/featureGroup/compose/LicenseFeatureGroupReachIndicator"
import LicenseFeatureGroupRegistryBrowser from "@/v2-backoffice-client/license/featureGroup/compose/registry/LicenseFeatureGroupRegistryBrowser"
import LicenseFeatureGroupSection from "@/v2-backoffice-client/license/featureGroup/compose/LicenseFeatureGroupSection"
import {
  isPersonLens,
  useFeatureGroupLens,
} from "@/v2-backoffice-client/license/featureGroup/compose/license.featureGroup.lens.context"
import {
  checkedByOf,
  describeCheckedBy,
  NOT_CHECKED_NOTE,
  type CheckedBy,
} from "@/v2-backoffice-client/license/featureGroup/compose/license.featureGroup.checkedBy.utils"
import {
  individualPicksOf,
  useCatalogPlacement,
} from "@/v2-backoffice-client/license/featureGroup/compose/license.featureGroup.compose.catalog.hooks"
import {
  pluralize,
  type FeatureGroupPickedLike,
} from "@/v2-backoffice-client/license/featureGroup/compose/license.featureGroup.compose.utils"

export type LicenseFeatureGroupIndividualSectionProps = {
  /** The draft, every pick; the section keeps the ones the catalogs do not place. */
  pickedFeatures: readonly FeatureGroupPickedLike[]
  /** As persisted; a pick the draft dropped stays in view so it can be restored. */
  persistedFeatures: readonly FeatureGroupPickedLike[]
  featureGroupName: string
  isPicked(code: string): boolean
  wasPicked(code: string): boolean
  onSetPicked(
    features: readonly FeatureGroupPickedLike[],
    picked: boolean,
  ): void
  className?: string
}

/** Why a pick sits here rather than in a catalog, and what it does for a user. */
function reasonOf(checkedBy: CheckedBy) {
  switch (checkedBy.kind) {
    case "nobody":
      return "No check reads this code yet, so the grant changes nothing for a user today. It is here so the package already says what it includes when the check goes live."
    case "server":
      return `A server checks it (${checkedBy.names}), not Fusion's own screens — so it has no place in the Capabilities or Content views and is granted here, one code at a time.`
    default:
      return "Not in a catalog right now — withdrawn, or without its catalog row — so the grant is dormant until it is."
  }
}

// The third view: single features granted one by one. Not collections like
// the capabilities, not classes like the content — one registry row each,
// for what the catalogs do not surface: a code a server checks (an MCP
// sign-in, the Agent's data answers), a layer not published yet, an
// exception for one customer's package. Each row says who checks it and
// what the grant does for a user; a removal can be undone until Save.
// Beneath, the registry itself, browsable by type and code family, is how
// more are found — by reading, not by knowing a code.
function LicenseFeatureGroupIndividualSection(
  props: LicenseFeatureGroupIndividualSectionProps,
) {
  const {
    pickedFeatures,
    persistedFeatures,
    featureGroupName,
    isPicked,
    wasPicked,
    onSetPicked,
    className,
  } = props
  const { catalogCodes, entitledTypeCodes } = useCatalogPlacement()
  const lens = useFeatureGroupLens()
  const picks = individualPicksOf(pickedFeatures, catalogCodes)
  const removed = individualPicksOf(persistedFeatures, catalogCodes).filter(
    (feature) => !isPicked(feature.Code),
  )
  const rows = [
    ...picks.map((feature) => ({ feature, isRemoved: false })),
    ...removed.map((feature) => ({ feature, isRemoved: true })),
  ]

  return (
    <LicenseFeatureGroupSection
      className={className}
      title="Individual features"
      summary={
        isPersonLens(lens)
          ? picks.length === 0
            ? `None: ${lens.customerName}'s own list grants no single registry features.`
            : `${pluralize(picks.length, "feature")} granted one by one on ${lens.customerName}'s own list; the mark says whether each reaches ${lens.userName}.`
          : picks.length === 0
            ? "None yet — pick from the registry below for what the catalogs do not surface."
            : `${pluralize(picks.length, "feature")} granted one by one.`
      }
      about={
        <>
          <p>
            One registry feature at a time — not a collection like a capability,
            not a class like the content. For what the catalogs do not surface:
            a layer not published yet, an exception for one customer&apos;s
            package.
          </p>
          <p className="mt-2">
            Each row says who checks the code: Fusion (a capability), the map
            (base maps and layers), or a server such as the Placepoint MCP or
            the Agent, which declare their checks in the catalog. A row marked
            &ldquo;{NOT_CHECKED_NOTE}&rdquo; is checked by nothing today, so
            granting it changes nothing for a user; it is in the catalog so a
            package can already say what it includes before that check is live.
          </p>
        </>
      }
    >
      {rows.length > 0 && (
        <ul className="max-h-48 shrink-0 divide-y overflow-y-auto rounded-md border">
          {rows.map(({ feature, isRemoved }) => {
            const isAdded = !isRemoved && !wasPicked(feature.Code)
            const checkedBy = checkedByOf({
              isSurface: feature.IsSurface,
              featureTypeCode: feature.FeatureTypeCode,
              entitledTypeCodes,
              checkedBy: feature.CheckedBy,
              checkedHow: feature.CheckedHow,
            })
            return (
              <li
                key={feature.Code}
                className={cn(
                  "flex items-center gap-2.5 border-l-2 border-l-transparent py-1.5 pl-2.5 pr-1.5",
                  (isAdded || isRemoved) && "border-l-primary bg-primary/5",
                )}
              >
                {isPersonLens(lens) && (
                  <LicenseFeatureGroupReachIndicator
                    state={lens.reaches(feature.Code) ? "all" : "none"}
                    title={
                      lens.reaches(feature.Code)
                        ? `Reaches ${lens.userName}: on ${lens.customerName}'s own list, carried by a live license.`
                        : `Does not reach ${lens.userName}: on ${lens.customerName}'s own list, but no live license carries it to them.`
                    }
                  />
                )}
                <LicenseFeatureGroupDetailsHoverCard
                  title={feature.Name ?? feature.Code}
                  code={feature.Code}
                  facts={[
                    { label: "Kind", value: feature.FeatureTypeName ?? "—" },
                    {
                      label: "Checked by",
                      value: describeCheckedBy(checkedBy),
                    },
                    { label: "Why here", value: reasonOf(checkedBy) },
                  ]}
                >
                  <span className="min-w-0 flex-1">
                    <span
                      className={cn(
                        "block truncate text-xs",
                        isRemoved && "line-through text-muted-foreground",
                      )}
                    >
                      {feature.Name ?? feature.Code}
                    </span>
                    <span className="block truncate font-mono text-[11px] text-muted-foreground">
                      {feature.Code}
                    </span>
                  </span>
                </LicenseFeatureGroupDetailsHoverCard>
                {(isAdded || isRemoved) && (
                  <span className="shrink-0 text-[11px] text-primary">
                    {isRemoved ? "removed" : "added"}
                  </span>
                )}
                <LicenseFeatureGroupCheckedByMark checkedBy={checkedBy} />
                {feature.FeatureTypeName && (
                  <Badge size="xs" variant="outline">
                    {feature.FeatureTypeName}
                  </Badge>
                )}
                {isPersonLens(lens) ? null : isRemoved ? (
                  <Button
                    type="button"
                    size="xs"
                    variant="ghost"
                    data-feature-code="UI_BackofficeFeatureGroup_Specifics_Restore"
                    onClick={() => {
                      onSetPicked([feature], true)
                    }}
                  >
                    Restore
                  </Button>
                ) : (
                  <TooltipButton
                    size="xs"
                    variant="ghost"
                    isSquare
                    iconSrc="cross"
                    content={`Remove ${feature.Name ?? feature.Code} from ${featureGroupName}`}
                    data-feature-code="UI_BackofficeFeatureGroup_Specifics_Remove"
                    onClick={() => {
                      onSetPicked([feature], false)
                    }}
                  />
                )}
              </li>
            )
          })}
        </ul>
      )}

      {/* The registry is where more picks are found; a person's view adds none. */}
      {!isPersonLens(lens) && (
        <LicenseFeatureGroupRegistryBrowser
          className="min-h-0 flex-1"
          entitledTypeCodes={entitledTypeCodes}
          featureGroupName={featureGroupName}
          isPicked={isPicked}
          wasPicked={wasPicked}
          onSetPicked={onSetPicked}
        />
      )}
    </LicenseFeatureGroupSection>
  )
}

export default LicenseFeatureGroupIndividualSection
